import React, { useState, useEffect } from 'react';
import { Search, X, ArrowUpDown, Pencil, Trash2, Download } from 'lucide-react';
import { useNavigate } from "react-router-dom";
import Select from 'react-select';
import { tasksAPI, employeesAPI, contactsAPI, companiesAPI } from "../../../services/api";
import { toast } from "react-toastify";
import { exportTasksToExcel } from "../../../utils/ExportUtils";
import CreateTaskModal from "./CreateTaskModal";
import TaskDeleteModal from "./TaskDeleteModal";
import TaskAssignModal from "./TaskAssignModal";
import TaskBulkEditModal from "./TaskEditBulkModal";
import TaskEditDrawer from "./TaskEditDrawer";
import PageNavigator from "../../common/PageNavigator";

const PAGE_SIZE = 20;

const Tasks = () => {
  const [activeTab, setActiveTab] = useState('All Tasks');
  const [tasks, setTasks] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const navigate = useNavigate();

  // ── Bulk modal state ───────────────────────────────────────────────────────
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);

  // ── Single-row action state ────────────────────────────────────────────────
  const [taskToEdit, setTaskToEdit] = useState(null);
  const [showEditDrawer, setShowEditDrawer] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [showSingleDeleteModal, setShowSingleDeleteModal] = useState(false);

  // ── Inline editing state ──────────────────────────────────────────────────
  const [editingCell, setEditingCell] = useState(null);
  const [editingValue, setEditingValue] = useState('');

  // ── Name resolution caches: { [id]: "Resolved Name" } ────────────────────
  const [employeeCache, setEmployeeCache] = useState({});
  const [contactCache,  setContactCache]  = useState({});
  const [companyCache,  setCompanyCache]  = useState({});

  // Visible columns
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('taskViewColumns');
    return saved ? JSON.parse(saved) : [
      'subject', 'type', 'dueDate', 'priority', 'assignedTo',
      'status', 'linkedContact', 'linkedAccount', 'createdAt'
    ];
  });

  const allColumns = [
    { key: 'subject',       label: 'Subject',        required: true },
    { key: 'type',          label: 'Type' },
    { key: 'dueDate',       label: 'Due Date' },
    { key: 'priority',      label: 'Priority' },
    { key: 'assignedTo',    label: 'Assigned To' },
    { key: 'status',        label: 'Status' },
    { key: 'linkedContact', label: 'Linked Contact' },
    { key: 'linkedAccount', label: 'Linked Account' },
    {key:'notes',label:'Notes'},
    {key:'description',label:'Description'},
    { key: 'createdAt',     label: 'Created At' }
  ];

  const [filters, setFilters] = useState({
    type: null, priority: null, status: null,
    assignedTo: null, dueDateRange: null
  });

  const tabs = ['All Tasks', 'My Tasks'];

  const typeOptions = [
    { value: 'EMAIL', label: 'Email' },
    { value: 'TODO',  label: 'To-Do' },
    { value: 'CALL',  label: 'Call' }
  ];
  const priorityOptions = [
    { value: 'HIGH',   label: 'High' },
    { value: 'MEDIUM', label: 'Medium' },
    { value: 'LOW',    label: 'Low' },
    { value: 'NONE',   label: 'None' }
  ];
  const statusOptions = [
    { value: 'PENDING',     label: 'Pending' },
    { value: 'IN_PROGRESS', label: 'In Progress' },
    { value: 'COMPLETED',   label: 'Completed' },
    { value: 'CANCELLED',   label: 'Cancelled' }
  ];
  const dueDateRangeOptions = [
    { value: 'TODAY',      label: 'Today' },
    { value: 'TOMORROW',   label: 'Tomorrow' },
    { value: 'THIS_WEEK',  label: 'This Week' },
    { value: 'THIS_MONTH', label: 'This Month' },
    { value: 'OVERDUE',    label: 'Overdue' }
  ];

  // ── Resolve employee names whenever tasks change ───────────────────────────
  useEffect(() => {
    const ids = [...new Set(tasks.map(t => t.assignedTo).filter(Boolean))];
    const uncached = ids.filter(id => !(id in employeeCache));
    if (!uncached.length) return;
    uncached.forEach(async (id) => {
      try {
        const res = await employeesAPI.getEmployeeById(id);
        const emp = res.data;
        const name = emp.name || `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || emp.email || id;
        setEmployeeCache(prev => ({ ...prev, [id]: name }));
      } catch {
        setEmployeeCache(prev => ({ ...prev, [id]: id }));
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks]);

  // ── Resolve contact names whenever tasks change ────────────────────────────
  useEffect(() => {
    const ids = [...new Set(tasks.map(t => t.linkedContact).filter(Boolean))];
    const uncached = ids.filter(id => !(id in contactCache));
    if (!uncached.length) return;
    uncached.forEach(async (id) => {
      try {
        const res = await contactsAPI.getContactById(id);
        const c = res.data;
        const name = c.name || `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.email || id;
        setContactCache(prev => ({ ...prev, [id]: name }));
      } catch {
        setContactCache(prev => ({ ...prev, [id]: id }));
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks]);

  // ── Resolve company names whenever tasks change ────────────────────────────
  useEffect(() => {
    const ids = [...new Set(tasks.map(t => t.linkedAccount).filter(Boolean))];
    const uncached = ids.filter(id => !(id in companyCache));
    if (!uncached.length) return;
    uncached.forEach(async (id) => {
      try {
        const res = await companiesAPI.getCompanyById(id);
        const c = res.data;
        const name = c.companyName || c.name || id;
        setCompanyCache(prev => ({ ...prev, [id]: name }));
      } catch {
        setCompanyCache(prev => ({ ...prev, [id]: 'Linked company unavailable' }));
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks]);

  // ── Spinner shown while name is loading ───────────────────────────────────
  const LoadingSpinner = () => (
    <span className="flex items-center gap-1 text-gray-400 text-xs">
      <span className="w-3 h-3 border border-gray-300 border-t-transparent rounded-full animate-spin inline-block" />
      Loading...
    </span>
  );

  // ── Inline editing: enum options ──────────────────────────────────────────
  const enumFields = {
    type: [
      { value: 'CALL',      label: 'Call' },
      { value: 'EMAIL',     label: 'Email' },
      { value: 'MEETING',   label: 'Meeting' },
      { value: 'FOLLOW_UP', label: 'Follow Up' },
      { value: 'DEMO',      label: 'Demo' },
      { value: 'OTHER',     label: 'Other' }
    ],
    priority: [
      { value: 'HIGH',   label: 'High' },
      { value: 'MEDIUM', label: 'Medium' },
      { value: 'LOW',    label: 'Low' }
    ],
    status: [
      { value: 'PENDING',     label: 'Pending' },
      { value: 'IN_PROGRESS', label: 'In Progress' },
      { value: 'COMPLETED',   label: 'Completed' },
      { value: 'CANCELLED',   label: 'Cancelled' }
    ]
  };

  const dateFields     = ['dueDate'];
  // assignedTo / linkedContact / linkedAccount are read-only inline
  // (they require ID lookups — edit via the drawer instead)
  const readOnlyFields = ['subject', 'id', 'createdAt', 'updatedAt',
                          'assignedTo', 'linkedContact', 'linkedAccount'];

  const getOptionsForColumn = (columnKey) => enumFields[columnKey] || null;
  const isEnumField = (columnKey) => columnKey in enumFields;
  const isDateField = (columnKey) => dateFields.includes(columnKey);

  const handleCellDoubleClick = (task, columnKey) => {
    if (readOnlyFields.includes(columnKey)) return;
    setEditingCell({ rowId: task.id, columnKey });
    if (isDateField(columnKey) && task[columnKey]) {
      setEditingValue(new Date(task[columnKey]).toISOString().slice(0, 16));
    } else {
      setEditingValue(task[columnKey] || '');
    }
  };

  const handleCellInputChange  = (e) => setEditingValue(e.target.value);
  const handleCellSelectChange = (value) => setEditingValue(value);

  const handleCellSave = async () => {
    if (!editingCell) return;
    try {
      const task = tasks.find(t => t.id === editingCell.rowId);
      if (!task) return;
      const valueToSave = isDateField(editingCell.columnKey) && editingValue
        ? new Date(editingValue).toISOString().slice(0, 19)
        : editingValue;
      await tasksAPI.updateTask(task.id, { [editingCell.columnKey]: valueToSave });
      setTasks(prev => prev.map(t => t.id === task.id ? { ...t, [editingCell.columnKey]: valueToSave } : t));
      toast.success('Field updated successfully');
      setEditingCell(null);
      setEditingValue('');
    } catch (error) {
      toast.error('Failed to update field: ' + (error.response?.data?.message || error.message));
      setEditingCell(null);
    }
  };

  const handleCellCancel = () => { setEditingCell(null); setEditingValue(''); };
  const isEditingCell = (taskId, columnKey) =>
    editingCell?.rowId === taskId && editingCell?.columnKey === columnKey;

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchEmployeeOptions = async () => {
    try {
      const res = await employeesAPI.getAllEmployees();
      setEmployeeOptions((res.data || []).map(emp => ({
        value: emp.id,
        label: emp.name || `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || emp.email || emp.id
      })));
    } catch (error) { console.error('Failed to fetch employees:', error); }
  };

  const fetchTasksPaged = async (page) => {
    setLoading(true);
    try {
      const params = {
        page, size: PAGE_SIZE,
        search: searchQuery || undefined,
        type: filters.type?.value,
        priority: filters.priority?.value,
        status: filters.status?.value,
        assignedTo: filters.assignedTo?.value,
        dueDateRange: filters.dueDateRange?.value,
        ...(activeTab === 'My Tasks' ? { scope: 'mine' } : {})
      };
      const response = await tasksAPI.getTasksPaged(params);
      setTasks(response.data.content || []);
      setCurrentPage(response.data.currentPage);
      setTotalPages(response.data.totalPages);
      setTotalElements(response.data.totalElements);
    } catch (error) {
      toast.error('Failed to fetch tasks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEmployeeOptions(); }, []);

  useEffect(() => {
    setCurrentPage(0);
    fetchTasksPaged(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, activeTab, searchQuery]);

  useEffect(() => {
    fetchTasksPaged(currentPage);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const handleFilterChange = (name, value) => setFilters(prev => ({ ...prev, [name]: value }));
  const clearAllFilters = () => setFilters({ type: null, priority: null, status: null, assignedTo: null, dueDateRange: null });
  const hasActiveFilters = filters.type || filters.priority || filters.status || filters.assignedTo || filters.dueDateRange;

  const handleRowSelect = (id) => setSelectedRows(prev => prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]);
  const handleSelectAll = () => setSelectedRows(selectedRows.length === tasks.length ? [] : tasks.map(t => t.id));

  const handleExport = () => {
    const visibleCols = allColumns.filter(col => visibleColumns.includes(col.key));
    exportTasksToExcel(tasks, visibleCols);
  };

  // ── Bulk handlers ──────────────────────────────────────────────────────────
  const handleBulkDelete = async () => {
    try {
      await tasksAPI.bulkDelete(selectedRows);
      toast.success(`${selectedRows.length} task(s) deleted`);
      setSelectedRows([]);
      setShowDeleteModal(false);
      fetchTasksPaged(currentPage);
    } catch (e) { toast.error(e.response?.data?.message || "Failed to delete tasks"); }
  };

  const handleBulkAssign = async (newAssignee) => {
    try {
      await tasksAPI.bulkAssign(selectedRows, newAssignee);
      toast.success(`${selectedRows.length} task(s) assigned`);
      setSelectedRows([]);
      setShowAssignModal(false);
      fetchTasksPaged(currentPage);
    } catch (e) { toast.error(e.response?.data?.message || "Failed to assign tasks"); }
  };

  const handleBulkEdit = async (fieldName, fieldValue) => {
    try {
      const res = await tasksAPI.bulkUpdate(selectedRows, fieldName, fieldValue);
      const r = res.data || {};
      if (r.failedCount > 0) toast.warning(`${r.successCount} updated, ${r.failedCount} failed`);
      else toast.success(`${r.successCount ?? selectedRows.length} task(s) updated`);
      setSelectedRows([]);
      setShowBulkEditModal(false);
      fetchTasksPaged(currentPage);
    } catch (e) { toast.error(e.response?.data?.message || "Failed to update tasks"); }
  };

  // ── Single-row handlers ────────────────────────────────────────────────────
  const handleRowEditClick   = (task, e) => { e.stopPropagation(); setTaskToEdit(task); setShowEditDrawer(true); };
  const handleRowDeleteClick = (task, e) => { e.stopPropagation(); setTaskToDelete(task); setShowSingleDeleteModal(true); };

  const handleSingleSave = async (id, data) => {
    try {
      await tasksAPI.updateTask(id, data);
      toast.success("Task updated successfully");
      setShowEditDrawer(false);
      setTaskToEdit(null);
      fetchTasksPaged(currentPage);
    } catch (e) { toast.error(e.response?.data?.message || "Failed to update task"); }
  };

  const handleSingleDelete = async () => {
    try {
      await tasksAPI.deleteTask(taskToDelete.id);
      toast.success("Task deleted successfully");
      setShowSingleDeleteModal(false);
      setTaskToDelete(null);
      fetchTasksPaged(currentPage);
    } catch (e) { toast.error(e.response?.data?.message || "Failed to delete task"); }
  };

  // ── Column helpers ─────────────────────────────────────────────────────────
  const handleColumnToggle = (key) => setVisibleColumns(prev => {
    const next = prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key];
    localStorage.setItem('taskViewColumns', JSON.stringify(next));
    return next;
  });
  const handleSelectAllColumns   = () => { const k = allColumns.map(c => c.key); setVisibleColumns(k); localStorage.setItem('taskViewColumns', JSON.stringify(k)); };
  const handleDeselectAllColumns = () => { const k = allColumns.filter(c => c.required).map(c => c.key); setVisibleColumns(k); localStorage.setItem('taskViewColumns', JSON.stringify(k)); };
  const isColumnVisible = (key) => visibleColumns.includes(key);

  const getStatusColor   = (s) => ({ PENDING: 'bg-yellow-100 text-yellow-800', IN_PROGRESS: 'bg-blue-100 text-blue-800', COMPLETED: 'bg-green-100 text-green-800', CANCELLED: 'bg-red-100 text-red-800' }[s] || 'bg-gray-100 text-gray-800');
  const getPriorityColor = (p) => ({ HIGH: 'bg-red-100 text-red-800', MEDIUM: 'bg-yellow-100 text-yellow-800', LOW: 'bg-green-100 text-green-800' }[p] || 'bg-gray-100 text-gray-800');
  const formatDate = (d) => d ? new Date(d).toLocaleDateString() : 'N/A';

  // ── Cell content renderer ──────────────────────────────────────────────────
  const renderCellContent = (column, task) => {
    switch (column.key) {

      case 'subject':
        return (
          <button onClick={() => navigate(`/task/${task.id}`)}
            className="font-medium text-blue-600 hover:text-blue-700 hover:underline cursor-pointer">
            {task.subject}
          </button>
        );

      case 'type':
        return task.type
          ? <span className="text-gray-700">{task.type.replace('_', ' ')}</span>
          : <span className="text-gray-400">—</span>;

      case 'dueDate':
        return formatDate(task.dueDate);

      case 'priority':
        return task.priority
          ? <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(task.priority)}`}>{task.priority}</span>
          : <span className="text-gray-400">—</span>;

      case 'status':
        return task.status
          ? <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(task.status)}`}>{task.status.replace('_', ' ')}</span>
          : <span className="text-gray-400">—</span>;

      // ── Assigned To: employee ID → resolved name ─────────────────────────
      case 'assignedTo': {
        const id = task.assignedTo;
        if (!id) return <span className="text-gray-400">—</span>;
        const name = employeeCache[id];
        if (!name) return <LoadingSpinner />;
        return (
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-400 to-indigo-400 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
              {name.charAt(0).toUpperCase()}
            </div>
            <span className="text-sm text-gray-800">{name}</span>
          </div>
        );
      }

      // ── Linked Contact: contact ID → resolved full name ──────────────────
      case 'linkedContact': {
        const id = task.linkedContact;
        if (!id) return <span className="text-gray-400">—</span>;
        const name = contactCache[id];
        if (!name) return <LoadingSpinner />;
        return (
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-green-400 to-teal-400 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
              {name.charAt(0).toUpperCase()}
            </div>
            <span className="text-sm text-gray-800">{name}</span>
          </div>
        );
      }

      // ── Linked Account: company ID → resolved company name ───────────────
      case 'linkedAccount': {
        const id = task.linkedAccount;
        if (!id) return <span className="text-gray-400">—</span>;
        const name = companyCache[id];
        if (!name) return <LoadingSpinner />;
        return (
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
              {name.charAt(0).toUpperCase()}
            </div>
            <span className="text-sm text-gray-800">{name}</span>
          </div>
        );
      }

      case 'createdAt':
        return formatDate(task.createdAt);

      default:
        return task[column.key] || <span className="text-gray-400">—</span>;
    }
  };

  // ── Inline cell editor ─────────────────────────────────────────────────────
  const renderInlineEditor = (column) => {
    const isEnum = isEnumField(column.key);
    const isDate = isDateField(column.key);
    const options = getOptionsForColumn(column.key);

    return (
      <div className="flex items-center gap-1">
        {isEnum ? (
          <select
            value={editingValue}
            onChange={(e) => handleCellSelectChange(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleCellSave(); if (e.key === 'Escape') handleCellCancel(); }}
            className="flex-1 px-2 py-1 border border-blue-500 rounded bg-white text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
            autoFocus
          >
            <option value="">Select {column.label}</option>
            {options.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        ) : isDate ? (
          <input
            type="datetime-local"
            value={editingValue}
            onChange={handleCellInputChange}
            onKeyDown={(e) => { if (e.key === 'Enter') handleCellSave(); if (e.key === 'Escape') handleCellCancel(); }}
            className="flex-1 px-2 py-1 border border-blue-500 rounded bg-white text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
            autoFocus
          />
        ) : (
          <input
            type="text"
            value={editingValue}
            onChange={handleCellInputChange}
            onKeyDown={(e) => { if (e.key === 'Enter') handleCellSave(); if (e.key === 'Escape') handleCellCancel(); }}
            className="flex-1 px-2 py-1 border border-blue-500 rounded bg-white text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
            autoFocus
          />
        )}
        <button onClick={handleCellSave}   className="text-green-600 hover:text-green-700 font-bold text-xs flex-shrink-0" title="Save (Enter)">&#10003;</button>
        <button onClick={handleCellCancel} className="text-red-600 hover:text-red-700 font-bold text-xs flex-shrink-0"   title="Cancel (Esc)">&#x2715;</button>
      </div>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div>
      <div className="bg-white border-b">

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Tasks</h1>
            <p className="text-sm text-gray-600">{totalElements} records</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
              Create task
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center border-b">
          {tabs.map((tab) => (
            <button key={tab} onClick={() => { setActiveTab(tab); setSelectedRows([]); setCurrentPage(0); }}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>
              {tab}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-4 px-6">
            <button onClick={() => setShowColumnSelector(true)} className="text-sm text-blue-600 hover:text-blue-700">
              Customize Columns
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="p-4 border-b">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="w-40"><Select options={typeOptions} value={filters.type} onChange={v => handleFilterChange('type', v)} placeholder="Type" isClearable className="text-sm" /></div>
              <div className="w-40"><Select options={priorityOptions} value={filters.priority} onChange={v => handleFilterChange('priority', v)} placeholder="Priority" isClearable className="text-sm" /></div>
              <div className="w-44"><Select options={statusOptions} value={filters.status} onChange={v => handleFilterChange('status', v)} placeholder="Status" isClearable className="text-sm" /></div>
              <div className="w-44"><Select options={dueDateRangeOptions} value={filters.dueDateRange} onChange={v => handleFilterChange('dueDateRange', v)} placeholder="Due Date" isClearable className="text-sm" /></div>
              <div className="w-48"><Select options={employeeOptions} value={filters.assignedTo} onChange={v => handleFilterChange('assignedTo', v)} placeholder="Assigned To" isClearable className="text-sm" /></div>
              {hasActiveFilters && (
                <button onClick={clearAllFilters} className="text-sm text-red-600 border border-red-300 px-3 py-2 rounded-md">Clear All</button>
              )}
            </div>
          </div>

          {/* Search + Bulk Actions */}
          <div className="flex items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input type="text" placeholder="Search subject or description" value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
            <div className="flex items-center gap-3 ml-4">
              <button onClick={handleExport}
                className="px-4 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center gap-2">
                <Download size={16} /> Export
              </button>
              <button
                onClick={() => { if (!selectedRows.length) { alert("Select at least one task to edit"); return; } setShowBulkEditModal(true); }}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
                Edit
              </button>
              <button
                onClick={() => { if (!selectedRows.length) { alert("Select at least one task to delete"); return; } setShowDeleteModal(true); }}
                className="px-4 py-2 text-sm bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors">
                Delete
              </button>
              <button
                onClick={() => { if (!selectedRows.length) { alert("Select at least one task to assign"); return; } setShowAssignModal(true); }}
                className="px-4 py-2 text-sm bg-purple-500 text-white rounded-md hover:bg-purple-600 transition-colors">
                Assign
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto bg-white">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading tasks...</div>
          ) : tasks.length === 0 ? (
            <div className="p-8 text-center">
              <div className="max-w-md mx-auto">
                <h2 className="text-xl font-semibold text-gray-700 mb-2">You're all caught up on tasks.</h2>
                <p className="text-gray-500">Nice work.</p>
              </div>
            </div>
          ) : (
            <table className="w-full min-w-max">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="w-12 p-3 bg-gray-50">
                    <input type="checkbox"
                      checked={selectedRows.length === tasks.length && tasks.length > 0}
                      onChange={handleSelectAll}
                      className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" />
                  </th>
                  {allColumns.filter(col => isColumnVisible(col.key)).map((col) => (
                    <th key={col.key} className="text-left p-4 text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[140px]">
                      <div className="flex items-center gap-2">{col.label}<ArrowUpDown size={12} className="text-gray-400" /></div>
                    </th>
                  ))}
                  <th className="p-4 text-xs font-medium text-gray-500 uppercase tracking-wider text-left w-20 sticky right-0 bg-gray-50 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.04)]">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {tasks.map((task) => (
                  <tr key={task.id}
                    className={`group transition-colors ${selectedRows.includes(task.id) ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                    <td className="p-3">
                      <input type="checkbox" checked={selectedRows.includes(task.id)}
                        onChange={() => handleRowSelect(task.id)}
                        className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" />
                    </td>

                    {allColumns.filter(col => isColumnVisible(col.key)).map((col) => (
                      <td
                        key={col.key}
                        className={`p-3 text-sm text-gray-700 transition-colors ${
                          readOnlyFields.includes(col.key)
                            ? 'cursor-default'
                            : 'cursor-text hover:bg-blue-50'
                        }`}
                        onDoubleClick={() => handleCellDoubleClick(task, col.key)}
                        title={!readOnlyFields.includes(col.key) ? 'Double-click to edit' : undefined}
                      >
                        {isEditingCell(task.id, col.key)
                          ? renderInlineEditor(col)
                          : renderCellContent(col, task)
                        }
                      </td>
                    ))}

                    {/* Row actions */}
                    <td className="p-3 whitespace-nowrap sticky right-0 bg-white group-hover:bg-gray-50 transition-colors shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.04)]" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        <button onClick={(e) => handleRowEditClick(task, e)} title="Edit task"
                          className="p-1.5 rounded-md text-blue-600 hover:bg-blue-100 transition-all duration-150">
                          <Pencil size={18} />
                        </button>
                        <button onClick={(e) => handleRowDeleteClick(task, e)} title="Delete task"
                          className="p-1.5 rounded-md text-red-600 hover:bg-red-100 transition-all duration-150">
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        <PageNavigator
          currentPage={currentPage}
          totalPages={totalPages}
          totalElements={totalElements}
          pageSize={PAGE_SIZE}
          onPageChange={setCurrentPage}
        />
      </div>

      {/* Column Selector Modal */}
      {showColumnSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Customize Columns</h3>
              <button onClick={() => setShowColumnSelector(false)}><X size={20} /></button>
            </div>
            <div className="p-4">
              <div className="flex gap-2 mb-4">
                <button onClick={handleSelectAllColumns} className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50">Select all</button>
                <button onClick={handleDeselectAllColumns} className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50">Deselect all</button>
              </div>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {allColumns.map(col => (
                  <label key={col.key} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                    <input type="checkbox" checked={isColumnVisible(col.key)}
                      onChange={() => !col.required && handleColumnToggle(col.key)}
                      disabled={col.required}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                    <span className="text-sm">{col.label}</span>
                    {col.required && <span className="text-xs text-gray-500">(Required)</span>}
                  </label>
                ))}
              </div>
            </div>
            <div className="p-4 border-t flex justify-end">
              <button onClick={() => setShowColumnSelector(false)}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">Done</button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Modals */}
      {showDeleteModal   && <TaskDeleteModal   count={selectedRows.length} onClose={() => setShowDeleteModal(false)}   onConfirm={handleBulkDelete} />}
      {showAssignModal   && <TaskAssignModal   count={selectedRows.length} onClose={() => setShowAssignModal(false)}   onConfirm={handleBulkAssign} />}
      {showBulkEditModal && <TaskBulkEditModal count={selectedRows.length} onClose={() => setShowBulkEditModal(false)} onConfirm={handleBulkEdit} />}

      {/* Single-row delete */}
      {showSingleDeleteModal && taskToDelete && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden">
            <div className="px-6 py-5 border-b border-gray-100">
              <h3 className="text-base font-semibold text-gray-900">Delete Task</h3>
            </div>
            <div className="px-6 py-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Trash2 size={18} className="text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-700">
                    Delete task <span className="font-semibold text-gray-900">"{taskToDelete.subject}"</span>?
                  </p>
                  <p className="text-xs text-gray-500 mt-1">This action cannot be undone.</p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
              <button onClick={() => { setShowSingleDeleteModal(false); setTaskToDelete(null); }}
                className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors font-medium text-gray-600">
                Cancel
              </button>
              <button onClick={handleSingleDelete}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create */}
      {showCreateModal && <CreateTaskModal onClose={() => setShowCreateModal(false)} onCreate={() => { setShowCreateModal(false); fetchTasksPaged(currentPage); }} />}

      {/* Single edit drawer */}
      {showEditDrawer && taskToEdit && (
        <TaskEditDrawer
          task={taskToEdit}
          onClose={() => { setShowEditDrawer(false); setTaskToEdit(null); }}
          onSave={handleSingleSave}
        />
      )}
    </div>
  );
};

export default Tasks;